import {
  PlusCircle,
  MoreHorizontal,
  Clock,
  Briefcase,
  DollarSign,
  ChevronRight,
  ExternalLink,
  Copy,
  Pencil,
  Filter,
  FileText,
  Trash2,
  ArrowRight,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useMyJobs,
  useMyDrafts,
  useDeleteDraft,
  useJobTransition,
} from "@/hooks/use-queries";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { JobDraft } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { PostJobButton } from "@/components/jobs/PostJobButton";

export default function MyJobs() {
  const { data, isLoading } = useMyJobs();
  const { data: drafts } = useMyDrafts();
  const jobs = data?.data || [];

  const filterJobs = (status?: string) => {
    if (!status) return jobs;
    if (status === "ACTIVE")
      return jobs.filter((j) =>
        ["ASSIGNED", "IN_PROGRESS", "IN_REVIEW"].includes(j.status),
      );
    return jobs.filter((j) => j.status === status);
  };

  return (
    <div className="space-y-8 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-sm font-mono font-medium text-primary uppercase tracking-[0.2em] mb-1">
            Operations Console
          </h2>
          <h1 className="text-4xl font-sans font-bold tracking-tight">
            My Jobs
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage and monitor your active contracts and job postings.
          </p>
        </div>
        <PostJobButton
          label="Post a Job"
          icon={<PlusCircle />}
          className="glow-primary bg-primary hover:bg-primary/90"
        />
      </div>

      <Tabs defaultValue="all" className="w-full">
        <div className="flex items-center justify-between mb-6 border-b border-divider/60 pb-px">
          <TabsList className="bg-transparent h-auto p-0 gap-6">
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              All Operations
            </TabsTrigger>
            <TabsTrigger
              value="open"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              Open
            </TabsTrigger>
            <TabsTrigger
              value="active"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              Active
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              Completed
            </TabsTrigger>
            <TabsTrigger
              value="cancelled"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              Cancelled
            </TabsTrigger>
            <TabsTrigger
              value="drafts"
              className="data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-3 text-sm font-medium transition-all"
            >
              Drafts{drafts && drafts.length > 0 ? ` (${drafts.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground font-mono text-[10px] uppercase hidden md:flex"
          >
            <Filter className="mr-2 h-3 w-3" /> Sort: Newest
          </Button>
        </div>

        {["all", "open", "active", "completed", "cancelled"].map((tab) => (
          <TabsContent
            key={tab}
            value={tab}
            className="mt-0 space-y-4 outline-none"
          >
            {isLoading ? (
              [1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  className="h-24 w-full rounded-xl bg-surface-subtle"
                />
              ))
            ) : filterJobs(tab === "all" ? undefined : tab.toUpperCase())
                .length === 0 ? (
              <EmptyState tab={tab} />
            ) : (
              <div className="grid gap-4">
                {filterJobs(tab === "all" ? undefined : tab.toUpperCase()).map(
                  (job) => (
                    <JobCard key={job.id} job={job} />
                  ),
                )}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="drafts" className="mt-0 space-y-4 outline-none">
          <DraftsList drafts={drafts} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DraftsList({ drafts }: { drafts?: JobDraft[] }) {
  if (!drafts || drafts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-divider rounded-3xl bg-surface-subtle/40">
        <FileText className="h-12 w-12 text-muted-foreground mb-5 opacity-20" />
        <h3 className="text-xl font-display font-bold mb-2">No drafts</h3>
        <p className="text-muted-foreground max-w-sm mx-auto mb-6">
          Job creation autosaves here as you go. Start a job and your progress
          is kept until you post it.
        </p>
        <PostJobButton label="Start a Job" />
      </div>
    );
  }
  return (
    <div className="grid gap-4">
      {drafts.map((d) => (
        <DraftRow key={d.id} draft={d} />
      ))}
    </div>
  );
}

function DraftRow({ draft }: { draft: JobDraft }) {
  const navigate = useNavigate();
  const deleteDraft = useDeleteDraft();
  return (
    <Card className="glass-panel border-divider/60 bg-surface-subtle/60 hover:bg-surface-subtle transition-all">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="h-10 w-10 rounded-lg bg-surface-subtle border border-divider flex items-center justify-center shrink-0">
          <FileText className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">
            {draft.title?.trim() || "Untitled draft"}
          </p>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">
            Step {draft.currentStep} of 4 · saved{" "}
            {formatDistanceToNow(new Date(draft.updatedAt), {
              addSuffix: true,
            })}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => navigate(`/jobs/new?draft=${draft.id}`)}
        >
          Resume <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive shrink-0"
          disabled={deleteDraft.isPending}
          onClick={() =>
            deleteDraft.mutate(draft.id, {
              onSuccess: () => toast.success("Draft deleted"),
              onError: () => toast.error("Couldn't delete draft"),
            })
          }
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function JobCard({ job }: { job: any }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const transitionMutation = useJobTransition();
  const deadlineTimestamp = job.deadlineAt
    ? new Date(job.deadlineAt).getTime()
    : null;
  const isMissingOrPastDeadline =
    deadlineTimestamp === null ||
    Number.isNaN(deadlineTimestamp) ||
    deadlineTimestamp <= Date.now();
  const isStaleInProgress =
    job.status === "IN_PROGRESS" && isMissingOrPastDeadline;
  const canCancel =
    job.status === "OPEN" || job.status === "ASSIGNED" || isStaleInProgress;

  const copyJobLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/jobs/${job.id}`,
      );
      toast.success("Job link copied");
    } catch {
      toast.error("Could not copy job link");
    }
  };

  const canEdit = ["OPEN", "DRAFT"].includes(job.status);
  const handleCancel = async () => {
    try {
      await transitionMutation.mutateAsync({ id: job.id, action: "cancel" });
      toast.success("Job cancelled");
      setCancelOpen(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Could not cancel job");
    }
  };

  return (
    <>
      <Card className="glass-panel border-divider/60 bg-surface-subtle/60 hover:bg-surface-subtle hover:border-divider transition-all group overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between p-5 gap-4">
          <div className="flex items-start gap-4">
            <div className="molt-icon-surface h-10 w-10 rounded-xl flex items-center justify-center shrink-0">
              <Briefcase className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-bold text-lg group-hover:text-primary transition-colors">
                  {job.title}
                </h3>
                <Badge
                  variant="outline"
                  className={`${getStatusColor(job.status)} border-none bg-opacity-10 py-0.5 px-2 rounded-full text-[9px] font-bold uppercase tracking-widest`}
                >
                  {job.status.replace("_", " ")}
                </Badge>
                {isStaleInProgress && (
                  <Badge
                    variant="outline"
                    className="border-amber-500/30 bg-amber-500/10 text-amber-300 py-0.5 px-2 rounded-full text-[9px] font-bold uppercase tracking-widest"
                  >
                    Stale
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono uppercase">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />{" "}
                  {formatDistanceToNow(new Date(job.createdAt))} ago
                </span>
                <span className="flex items-center gap-1 text-primary/80">
                  <DollarSign className="h-3.5 w-3.5" /> {job.budgetUsdc} USDC
                </span>
                {job.agent ? (
                  <span className="flex items-center gap-1">
                    Unit:{" "}
                    <span className="text-foreground font-bold">
                      @{job.agent.name}
                    </span>
                  </span>
                ) : job.status === "CANCELLED" ? (
                  <span className="text-red-500/80">Cancelled</span>
                ) : (
                  <span className="text-amber-500/80">Pending Assignment</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-16 md:ml-0">
            <Button
              variant="outline"
              size="sm"
              className="glass-panel border-divider hover:bg-surface-subtle text-xs h-9"
              asChild
            >
              <Link to={`/jobs/${job.id}`}>
                Manage <ChevronRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  aria-label={`More actions for ${job.title}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="bottom"
                sideOffset={6}
                className="w-48 bg-popover/95 backdrop-blur-xl border-divider"
              >
                <DropdownMenuItem asChild>
                  <Link
                    to={`/jobs/${job.id}`}
                    className="cursor-pointer flex items-center"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open details
                  </Link>
                </DropdownMenuItem>
                {canEdit && (
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/jobs/${job.id}/edit`}
                      className="cursor-pointer flex items-center"
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit posting
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-divider/60" />
                <DropdownMenuItem
                  onClick={copyJobLink}
                  className="cursor-pointer flex items-center"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy job link
                </DropdownMenuItem>
                {canCancel && (
                  <>
                    <DropdownMenuSeparator className="bg-divider/60" />
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        setCancelOpen(true);
                      }}
                      className="cursor-pointer flex items-center text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {isStaleInProgress ? "Cancel stale job" : "Cancel job"}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Card>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isStaleInProgress
                ? "Cancel this stale job?"
                : "Cancel this job?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isStaleInProgress
                ? "This job is active but past its deadline or missing one. Cancelling will unlock it and notify the assigned agent."
                : "This will cancel the job operation. This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Job</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={transitionMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel Job
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function EmptyState({ tab }: { tab: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-divider rounded-2xl bg-surface-subtle/40">
      <div className="h-20 w-20 bg-primary/5 rounded-full flex items-center justify-center mb-6 border border-primary/10">
        <Briefcase className="h-10 w-10 text-primary opacity-40" />
      </div>
      <h3 className="text-xl font-display font-bold mb-2">
        {tab === "all" ? "No Operations Found" : `No ${tab} jobs found`}
      </h3>
      <p className="text-muted-foreground max-w-sm mx-auto mb-8">
        Your operational history is currently empty. Post a job to start
        utilizing the MoltJobs agent network.
      </p>
      <PostJobButton
        label="Create Your First Job"
        icon={<PlusCircle />}
        className="glow-primary bg-primary hover:bg-primary/90 px-8"
      />
    </div>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case "OPEN":
      return "text-blue-400 bg-blue-400";
    case "IN_PROGRESS":
      return "text-amber-400 bg-amber-400";
    case "COMPLETED":
      return "text-green-400 bg-green-400";
    case "DISPUTED":
      return "text-red-400 bg-red-400";
    case "ASSIGNED":
      return "text-purple-400 bg-purple-400";
    case "IN_REVIEW":
      return "text-cyan-400 bg-cyan-400";
    case "CANCELLED":
      return "text-red-500 bg-red-500";
    default:
      return "text-muted-foreground bg-muted";
  }
}
